/* eslint-disable @typescript-eslint/default-param-last */
import _ from 'lodash'
import type {ParsedStyleItem, Property} from '../types'
import {styleItemSelectorType} from '../parseStylableStyleItems'
import {markPropertyToRemove, isPropertyToRemove, removePropertyToRemovePrefix, rgbToHex, splitWithRgb} from './utils'

const SIDES = ['left', 'right', 'top', 'bottom'] as const
const BORDER_SIDES = ['border-left', 'border-right', 'border-top', 'border-bottom'] as const
const borderPropertiesToRemove = [
    'border',
    'border-width',
    'border-style',
    'border-color',
    ..._.flatMap(SIDES, side => [`border-${side}-style`, `border-${side}-color`]),
    ...BORDER_SIDES.map(markPropertyToRemove)
] as const

type BorderObjectType = {
    [key in (typeof SIDES)[number]]: string
}

const updateBorderWidth = (borderValue: string = '1px solid', borderWidth: string): string => {
    const [, style, ...colorSplit] = borderValue.split(' ')
    const color = colorSplit.join(' ')
    return color ? `${borderWidth} ${style} ${color}` : `${borderWidth} ${style}`
}

const updateBorderStyle = (borderValue: string = '1px solid', borderStyle: string): string => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [width, _style, ...colorSplit] = borderValue.split(' ')
    const color = colorSplit.join(' ')
    return color ? `${width} ${borderStyle} ${color}` : `${width} ${borderStyle}`
}

const updateBorderColor = (borderValue: string = '1px solid', borderColor: string): string => {
    const [width, style] = borderValue.split(' ')
    const rgbRegex = /^(rgb|rgba)\(\d/
    const color = rgbRegex.test(borderColor) ? rgbToHex(borderColor) : borderColor
    return `${width} ${style} ${color}`
}

const updateBorderPartInBorderObject = (
    borderObject: BorderObjectType,
    borderPartProp: Property,
    updateBorderPartFunc: (borderValue: string, newValue: string) => string
): BorderObjectType => {
    const borderPart = splitWithRgb(borderPartProp.value)
    const bordersLength = borderPart.length

    if (bordersLength === 1) {
        const [borderPartValue] = borderPart
        return {
            left: updateBorderPartFunc(borderObject.left, borderPartValue),
            right: updateBorderPartFunc(borderObject.right, borderPartValue),
            top: updateBorderPartFunc(borderObject.top, borderPartValue),
            bottom: updateBorderPartFunc(borderObject.bottom, borderPartValue)
        }
    }

    if (bordersLength === 2) {
        const [borderTopAndBottom, borderLeftAndRight] = borderPart
        return {
            left: updateBorderPartFunc(borderObject.left, borderLeftAndRight),
            right: updateBorderPartFunc(borderObject.right, borderLeftAndRight),
            top: updateBorderPartFunc(borderObject.top, borderTopAndBottom),
            bottom: updateBorderPartFunc(borderObject.bottom, borderTopAndBottom)
        }
    }

    if (bordersLength === 3) {
        const [borderTop, borderLeftAndRight, borderBottom] = borderPart
        return {
            left: updateBorderPartFunc(borderObject.left, borderLeftAndRight),
            right: updateBorderPartFunc(borderObject.right, borderLeftAndRight),
            top: updateBorderPartFunc(borderObject.top, borderTop),
            bottom: updateBorderPartFunc(borderObject.bottom, borderBottom)
        }
    }

    const [borderTop, borderRight, borderBottom, borderLeft] = borderPart

    return {
        left: updateBorderPartFunc(borderObject.left, borderLeft),
        right: updateBorderPartFunc(borderObject.right, borderRight),
        top: updateBorderPartFunc(borderObject.top, borderTop),
        bottom: updateBorderPartFunc(borderObject.bottom, borderBottom)
    }
}

const borderToProperty = (border: BorderObjectType): Property[] => {
    const borderProperties: Property[] = []
    Object.entries(border).forEach(([side, value]) => {
        borderProperties.push({
            prop: `border-${side}`,
            cssProp: `border-${side}`,
            value
        })
    })

    return borderProperties
}

const updateBorderSides = (borderSides: BorderObjectType, updatedBorderSides: BorderObjectType): void => {
    borderSides.left = updatedBorderSides.left
    borderSides.right = updatedBorderSides.right
    borderSides.top = updatedBorderSides.top
    borderSides.bottom = updatedBorderSides.bottom
}

const setAsRootProperties = (
    rootProperties: Property[] | null,
    borderSides: BorderObjectType,
    specificSide?: (typeof SIDES)[number]
): void => {
    const rootBorder = rootProperties?.find(prop => prop.cssProp === 'border')?.value
    ;(specificSide ? [specificSide] : SIDES).forEach(side => {
        borderSides[side] =
            borderSides[side] ?? (rootProperties?.find(prop => prop.cssProp === `border-${side}`)?.value || rootBorder)
    })
}

const handleBorderProperty = (
    property: Property,
    borderSides: BorderObjectType,
    rootProperties: Property[] | null
): void => {
    if (property.cssProp === 'border') {
        borderSides.left = property.value
        borderSides.right = property.value
        borderSides.top = property.value
        borderSides.bottom = property.value
    }

    if (property.cssProp === 'border-width') {
        setAsRootProperties(rootProperties, borderSides)
        const updatedBorder = updateBorderPartInBorderObject(borderSides, property, updateBorderWidth)
        updateBorderSides(borderSides, updatedBorder)
    }

    if (property.cssProp === 'border-style') {
        setAsRootProperties(rootProperties, borderSides)
        const updatedBorder = updateBorderPartInBorderObject(borderSides, property, updateBorderStyle)
        updateBorderSides(borderSides, updatedBorder)
    }

    if (property.cssProp === 'border-color') {
        setAsRootProperties(rootProperties, borderSides)
        const updatedBorder = updateBorderPartInBorderObject(borderSides, property, updateBorderColor)
        updateBorderSides(borderSides, updatedBorder)
    }

    SIDES.forEach(side => {
        if (property.cssProp === `border-${side}-style`) {
            setAsRootProperties(rootProperties, borderSides, side)
            const updatedBorder = updateBorderPartInBorderObject(borderSides, property, updateBorderStyle)
            borderSides[side] = updatedBorder[side]
        }

        if (property.cssProp === `border-${side}-color`) {
            setAsRootProperties(rootProperties, borderSides, side)
            const updatedBorder = updateBorderPartInBorderObject(borderSides, property, updateBorderColor)
            borderSides[side] = updatedBorder[side]
        }
    })

    if (BORDER_SIDES.includes(removePropertyToRemovePrefix(property.cssProp) as (typeof BORDER_SIDES)[number])) {
        const side = property.cssProp.split('-')[1] as (typeof SIDES)[number]
        borderSides[side] = property.value

        if (!isPropertyToRemove(property.cssProp)) {
            property.cssProp = markPropertyToRemove(property.cssProp)
        }
    }
}

const getUpdatedProperties = (properties: Property[], borderSides: BorderObjectType): Property[] => {
    return [
        ...properties.filter(property => !borderPropertiesToRemove.includes(property.cssProp)),
        ...borderToProperty(borderSides)
    ]
}

const migrateBorder = (styleItems: ParsedStyleItem[]): ParsedStyleItem[] => {
    return styleItems.map(styleItem => {
        const {parsedStyle} = styleItem
        const updatedParsedStyle = Object.entries(parsedStyle).reduce(
            (acc, [selector, {metadata, properties, mobileProperties}]) => {
                const borderSides = {} as BorderObjectType
                const mobileBorderSides = {} as BorderObjectType

                properties.forEach(property => {
                    const rootProperties =
                        metadata.type === styleItemSelectorType.STATE && metadata.root
                            ? acc[metadata.root].properties
                            : null
                    handleBorderProperty(property, borderSides, rootProperties)
                })

                mobileProperties.forEach(property => {
                    const rootProperties =
                        metadata.type === styleItemSelectorType.STATE && metadata.root
                            ? acc[metadata.root].mobileProperties
                            : null
                    handleBorderProperty(property, mobileBorderSides, rootProperties)
                })

                const updatedProperties = getUpdatedProperties(properties, borderSides)
                const updatedMobileProperties = getUpdatedProperties(mobileProperties, mobileBorderSides)

                return {
                    ...acc,
                    [selector]: {
                        ...acc[selector],
                        properties: updatedProperties,
                        mobileProperties: updatedMobileProperties
                    }
                }
            },
            parsedStyle
        )

        return {
            ...styleItem,
            parsedStyle: updatedParsedStyle
        }
    })
}

export default migrateBorder
